import _ from "lodash";
import AppConstants from "../constants/AppConstants";
import { findMostContrastedColors } from "./ColorUtils";
import { deepMerge } from "./mergeUtils";
import {
  DEFAULT_BUTTON_TEXT_VALUES,
  DEFAULT_BUTTON_BACKGROUND_VALUES,
} from "./Element/ButtonPropertyMaps";
import PROPERTY_MAPS from "@/constants/PropertyMaps";
import {
  BUTTON_BORDER_RADIUS,
  BUTTON_BORDER_STYLE,
  BUTTON_BORDER_COLOR,
  BUTTON_BORDER_WIDTH,
} from "@/constants/MergeConstants";
import { LAYOUT_DESKTOP, LAYOUT_MOBILE, SYNCED_PROPS } from "@/constants/DesignConstants.mjs";
import {
  FORM_STATE_ERROR,
  FORM_STATE_FOCUS,
  FORM_STYLE_IDS,
} from "@/constants/FormFieldConstants.mjs";
import { FORM_STYLE_OWN_PROPS } from "./Element/FormFieldPropertyMaps";
import { createFormStyleDefaults, DEFAULT_FORM_FONT } from "@/constants/FormStyleDefaults.mjs";

const {
  TRANSPARENT,
  BG_TYPE_COLOR,
  TEXT_STYLE_TITLE,
  TEXT_STYLE_HEADING,
  TEXT_STYLE_SUBHEADING,
  TEXT_STYLE_PARAGRAPH,
  BUTTON_STYLE_PRIMARY,
  BUTTON_STYLE_SECONDARY,
} = AppConstants;
const DEFAULT_BUTTON_MOBILE_PADDING = "7px 10px 7px 10px";
const DEFAULT_BUTTON_DESKTOP_PADDING = "10px 14px 10px 14px";
const TEXT_KEY_PREFIX = "text.";
const LINK_KEY_PREFIX = "link.";

const DEFAULT_BUTTON_BORDER_RADIUS = "10";
const DEFAULT_BUTTON_BORDER_WIDTH = "2";
const DEFAULT_BUTTON_BORDER_STYLE = "solid";

/**
 * Expands an object with dot notation keys into a nested object structure.
 *
 * @param {Object} input - The input object with dot notation keys
 * @returns {Object} A new object with nested structure based on the dot notation
 * @throws {Error} If there's a conflict where a non-object value would be overwritten with an object
 *
 * @example
 * expandDotNotation({ "text.color": "red", "text.size": 16 })
 * Returns { text: { color: "red", size: 16 } }
 */

export const expandDotNotation = (input) => {
  const result = {};

  for (const [key, value] of Object.entries(input)) {
    const parts = key.split(".");
    let current = result;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      if (i === parts.length - 1) {
        current[part] = value;
      } else {
        if (!(part in current)) {
          current[part] = {};
        } else if (typeof current[part] !== "object") {
          // Prevent overwriting a non-object with an object
          throw new Error(`Conflict at "${part}"`);
        }

        current = current[part];
      }
    }
  }

  return result;
};

const defaultButtonTextValues = expandDotNotation(DEFAULT_BUTTON_TEXT_VALUES);
const defaultButtonBackgroundValues = expandDotNotation(DEFAULT_BUTTON_BACKGROUND_VALUES);

/**
 * Creates default button styles for a theme, including primary and secondary button variants.
 * Generates contrasting colors and merges them with existing theme styles.
 *
 * @param {Object} theme - The theme object containing colors and styles
 *
 * @returns {Object} A new theme object with added button styles
 */
export const createDefaultButtonStyles = (theme) => {
  const themeWithButtons = { ...theme };
  const mostContrastedColors = findMostContrastedColors(theme.colors);
  const subheadingStyle = theme.styles.subheading || {};

  const primaryButtonTextChangesFromSubheading = {
    desktop: {
      c: `colors-${mostContrastedColors.pair[1]}`,
    },
  };

  const secondaryButtonTextChangesFromSubheading = {
    desktop: {
      c: `colors-${mostContrastedColors.pair[0]}`,
    },
    hover: {
      c: `colors-${mostContrastedColors.pair[0]}:70`,
    },
  };

  const primaryButtonTextChanges = deepMerge(
    subheadingStyle,
    primaryButtonTextChangesFromSubheading
  );

  const secondaryButtonTextChanges = deepMerge(
    subheadingStyle,
    secondaryButtonTextChangesFromSubheading
  );

  [TEXT_STYLE_TITLE, TEXT_STYLE_HEADING, TEXT_STYLE_SUBHEADING, TEXT_STYLE_PARAGRAPH].forEach(
    (textStyle) => {
      themeWithButtons.styles[textStyle] = {
        ...themeWithButtons.styles[textStyle],
        type: "text",
      };
    }
  );

  themeWithButtons.styles = {
    ...themeWithButtons.styles,
    [BUTTON_STYLE_PRIMARY]: {
      name: "Primary",
      id: "primary",
      type: "button",
      desktop: {
        ...defaultButtonBackgroundValues,
        bgcolor: `colors-${mostContrastedColors.pair[0]}`,
        fillType: BG_TYPE_COLOR,
        padding: DEFAULT_BUTTON_DESKTOP_PADDING,
        ...defaultButtonTextValues,
        text: primaryButtonTextChanges.desktop,
        hover: {
          c: `colors-${mostContrastedColors.pair[0]}:70`,
          dur: 0.5,
          text: primaryButtonTextChanges.hover || {},
        },
      },
      mobile: {
        padding: DEFAULT_BUTTON_MOBILE_PADDING,
        text: primaryButtonTextChanges.mobile,
      },
      sync: [
        ...PROPERTY_MAPS.BUTTON_SYNC_PROPS,
        ...(primaryButtonTextChanges.sync || [])
          ?.filter((syncProp) => !syncProp.includes(LINK_KEY_PREFIX))
          .map((syncProp) => `${TEXT_KEY_PREFIX}${syncProp}`),
      ],
    },
    [BUTTON_STYLE_SECONDARY]: {
      name: "Secondary",
      id: "secondary",
      type: "button",
      desktop: {
        ...defaultButtonBackgroundValues,
        bgcolor: TRANSPARENT,
        c: `colors-${mostContrastedColors.pair[0]}`,
        fillType: BG_TYPE_COLOR,
        padding: DEFAULT_BUTTON_DESKTOP_PADDING,
        ...defaultButtonTextValues,
        text: secondaryButtonTextChanges.desktop,
        hover: {
          dur: 0.5,
          text: secondaryButtonTextChanges.hover || {},
        },
      },
      mobile: {
        padding: DEFAULT_BUTTON_MOBILE_PADDING,
        text: secondaryButtonTextChanges.mobile,
      },
      sync: [
        ...PROPERTY_MAPS.BUTTON_SYNC_PROPS,
        ...(secondaryButtonTextChanges.sync  || [])
          ?.filter((syncProp) => !syncProp.includes(LINK_KEY_PREFIX))
          .map((syncProp) => `${TEXT_KEY_PREFIX}${syncProp}`),
      ],
    },
  };

  _.set(
    themeWithButtons,
    `styles.${BUTTON_STYLE_PRIMARY}.desktop.${BUTTON_BORDER_RADIUS}`,
    DEFAULT_BUTTON_BORDER_RADIUS
  );
  _.set(
    themeWithButtons,
    `styles.${BUTTON_STYLE_SECONDARY}.desktop.${BUTTON_BORDER_RADIUS}`,
    DEFAULT_BUTTON_BORDER_RADIUS
  );
  _.set(
    themeWithButtons,
    `styles.${BUTTON_STYLE_SECONDARY}.desktop.${BUTTON_BORDER_WIDTH}`,
    DEFAULT_BUTTON_BORDER_WIDTH
  );
  _.set(
    themeWithButtons,
    `styles.${BUTTON_STYLE_SECONDARY}.desktop.${BUTTON_BORDER_STYLE}`,
    DEFAULT_BUTTON_BORDER_STYLE
  );
  _.set(
    themeWithButtons,
    `styles.${BUTTON_STYLE_SECONDARY}.desktop.${BUTTON_BORDER_COLOR}`,
    `colors-${mostContrastedColors.pair[0]}`
  );
  _.set(
    themeWithButtons,
    `styles.${BUTTON_STYLE_SECONDARY}.desktop.hover.${BUTTON_BORDER_COLOR}`,
    `colors-${mostContrastedColors.pair[0]}:70`
  );

  return themeWithButtons;
};

/** @typedef {import("@/types/data/DesignTheme.mjs").DesignTheme} DesignTheme */

/**
 * Picks the form font: paragraph, then subheading, then fallback.
 * @param {DesignTheme} theme
 * @returns {string}
 */
export const resolveDefaultFormFont = (theme) =>
  _.get(theme, ["styles", TEXT_STYLE_PARAGRAPH, "desktop", "font"]) ||
  _.get(theme, ["styles", TEXT_STYLE_SUBHEADING, "desktop", "font"]) ||
  DEFAULT_FORM_FONT;

/**
 * True when the theme already defines every form style.
 * @param {DesignTheme} theme
 * @returns {boolean}
 */
export const hasAllFormStyles = (theme) =>
  FORM_STYLE_IDS.every((styleId) => Boolean(_.get(theme, ["styles", styleId])));

/**
 * Returns a new theme with any missing form styles added.
 * @param {DesignTheme} theme
 * @returns {DesignTheme}
 */
export const createDefaultFormStyles = (theme) => {
  const defaults = createFormStyleDefaults(resolveDefaultFormFont(theme));
  const themeWithForms = { ...theme, styles: { ...theme.styles } };

  FORM_STYLE_IDS.forEach((styleId) => {
    if (!themeWithForms.styles[styleId]) {
      themeWithForms.styles[styleId] = defaults[styleId];
    }
  });

  return themeWithForms;
};

const STATE_PREFIX = new RegExp(`^(${FORM_STATE_FOCUS}|${FORM_STATE_ERROR})\\.`);

/**
 * Keeps the parts of a layout that `isOwn` accepts, dropping any object left empty.
 * @param {Object} node
 * @param {string[]} path - The prop path down to `node`.
 * @param {(propName: string) => boolean} isOwn
 * @returns {Object}
 */
const keepOwnProps = (node, path, isOwn) =>
  Object.fromEntries(
    Object.entries(node).flatMap(([key, value]) => {
      const childPath = [...path, key];
      if (isOwn(childPath.join("."))) return [[key, value]];
      if (value === null || typeof value !== "object" || Array.isArray(value)) return [];

      const kept = keepOwnProps(value, childPath, isOwn);
      return Object.keys(kept).length ? [[key, kept]] : [];
    }),
  );

/**
 * Removes what the Dropdown, Radio and Checkbox entries store but their Design Settings tabs no
 * longer show, on both layouts and every state, with its `sync` entries, so those types inherit
 * the Input Field and Label values again. The tabs used to show them and wrote them there (#1567
 * hid them), where they still win over `field` but can't be changed.
 * @param {DesignTheme} theme - Plain JS; never mutated.
 * @returns {[DesignTheme, boolean]} The theme, and whether it changed.
 */
export const removeHiddenFormStyleProps = (theme) => {
  let dirty = false;
  const styles = { ...theme.styles };

  Object.entries(FORM_STYLE_OWN_PROPS).forEach(([styleId, ownProps]) => {
    const entry = styles[styleId];
    if (!entry) return;

    const isOwn = (propName) => {
      const base = propName.replace(STATE_PREFIX, "");
      return ownProps.some((own) => base === own || base.startsWith(`${own}.`));
    };
    const cleaned = { ...entry };
    [LAYOUT_DESKTOP, LAYOUT_MOBILE].forEach((layout) => {
      if (entry[layout]) cleaned[layout] = keepOwnProps(entry[layout], [], isOwn);
    });
    if (entry[SYNCED_PROPS]) cleaned[SYNCED_PROPS] = entry[SYNCED_PROPS].filter(isOwn);

    if (JSON.stringify(cleaned) !== JSON.stringify(entry)) {
      styles[styleId] = cleaned;
      dirty = true;
    }
  });

  return dirty ? [{ ...theme, styles }, true] : [theme, false];
};

export default createDefaultButtonStyles;
